import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Clock, User, DollarSign, TrendingUp, TrendingDown, ArrowRight,
  ChevronLeft, ChevronRight, ShoppingCart, Banknote, QrCode, CreditCard
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('id-ID').format(Math.round(v || 0));
const fmtRp = (v) => `Rp ${fmt(v)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '-'; } };
const fmtTime = (s) => { try { return new Date(s).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }); } catch { return '-'; } };
const fmtDuration = (opened, closed) => {
  try {
    const ms = new Date(closed) - new Date(opened);
    const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
    return `${h}j ${m}m`;
  } catch { return '-'; }
};

export default function POSShifts() {
  const { toast } = useToast();
  const [shifts, setShifts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailTx, setDetailTx] = useState([]);
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenant/pos/shifts', { params: { skip: page * limit, limit } });
      setShifts(res.data.shifts || []);
      setTotal(res.data.total || 0);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (shift) => {
    setDetail(shift);
    try {
      const res = await api.get(`/tenant/pos/shifts/${shift.id}`);
      setDetailTx(res.data.transactions || []);
    } catch { setDetailTx([]); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div className="space-y-6" data-testid="pos-shifts-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Clock size={24} /> Riwayat Shift POS</h1>
        <p className="text-muted-foreground mt-1">Lihat semua shift kasir yang pernah dibuka</p>
      </div>

      {/* Summary Cards */}
      {shifts.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Shift</p>
            <p className="text-2xl font-bold">{total}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Revenue</p>
            <p className="text-2xl font-bold text-emerald-600">{fmtRp(shifts.reduce((s, sh) => s + (sh.total_revenue || 0), 0))}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Transaksi</p>
            <p className="text-2xl font-bold">{shifts.reduce((s, sh) => s + (sh.total_transactions || 0), 0)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500">Selisih Kas Total</p>
            <p className={`text-2xl font-bold ${shifts.reduce((s, sh) => s + (sh.cash_difference || 0), 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {fmtRp(shifts.reduce((s, sh) => s + (sh.cash_difference || 0), 0))}
            </p>
          </CardContent></Card>
        </div>
      )}

      {/* Shifts Table */}
      <Card>
        <CardContent className="p-0">
          {shifts.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <Clock size={48} className="mx-auto mb-2" />
              <p>Belum ada riwayat shift</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Tanggal</th>
                    <th className="text-left p-3 font-medium text-slate-600">Kasir</th>
                    <th className="text-left p-3 font-medium text-slate-600">Status</th>
                    <th className="text-left p-3 font-medium text-slate-600">Durasi</th>
                    <th className="text-right p-3 font-medium text-slate-600">Kas Awal</th>
                    <th className="text-right p-3 font-medium text-slate-600">Kas Akhir</th>
                    <th className="text-right p-3 font-medium text-slate-600">Expected</th>
                    <th className="text-right p-3 font-medium text-slate-600">Selisih</th>
                    <th className="text-right p-3 font-medium text-slate-600">Revenue</th>
                    <th className="text-center p-3 font-medium text-slate-600">Tx</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map(s => (
                    <tr key={s.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(s)}>
                      <td className="p-3">{fmtDate(s.opened_at)}<br /><span className="text-xs text-slate-400">{fmtTime(s.opened_at)}</span></td>
                      <td className="p-3"><span className="flex items-center gap-1"><User size={14} /> {s.cashier_name}</span></td>
                      <td className="p-3">
                        <Badge variant={s.status === 'open' ? 'default' : 'secondary'} className={s.status === 'open' ? 'bg-emerald-100 text-emerald-700' : ''}>
                          {s.status === 'open' ? 'Buka' : 'Tutup'}
                        </Badge>
                      </td>
                      <td className="p-3">{s.closed_at ? fmtDuration(s.opened_at, s.closed_at) : '-'}</td>
                      <td className="p-3 text-right">{fmtRp(s.opening_cash)}</td>
                      <td className="p-3 text-right">{s.closing_cash !== null ? fmtRp(s.closing_cash) : '-'}</td>
                      <td className="p-3 text-right">{s.expected_cash !== null ? fmtRp(s.expected_cash) : '-'}</td>
                      <td className={`p-3 text-right font-medium ${(s.cash_difference || 0) < 0 ? 'text-red-600' : (s.cash_difference || 0) > 0 ? 'text-emerald-600' : ''}`}>
                        {s.cash_difference !== null ? fmtRp(s.cash_difference) : '-'}
                      </td>
                      <td className="p-3 text-right font-semibold text-emerald-600">{fmtRp(s.total_revenue)}</td>
                      <td className="p-3 text-center">{s.total_transactions || 0}</td>
                      <td className="p-3"><ArrowRight size={14} className="text-slate-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between p-3 border-t">
              <span className="text-sm text-slate-500">Menampilkan {page * limit + 1}–{Math.min((page + 1) * limit, total)} dari {total}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={14} />
                </Button>
                <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shift Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Shift — {detail?.cashier_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Dibuka</p><p className="font-medium text-sm">{fmtDate(detail.opened_at)} {fmtTime(detail.opened_at)}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Ditutup</p><p className="font-medium text-sm">{detail.closed_at ? `${fmtDate(detail.closed_at)} ${fmtTime(detail.closed_at)}` : 'Masih buka'}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Revenue</p><p className="font-bold text-emerald-600">{fmtRp(detail.total_revenue)}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Selisih Kas</p><p className={`font-bold ${(detail.cash_difference || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtRp(detail.cash_difference)}</p></div>
              </div>

              {/* Payment breakdown */}
              <div className="grid grid-cols-4 gap-3">
                <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg"><Banknote size={16} className="text-emerald-600" /><div><p className="text-xs text-slate-500">Tunai</p><p className="font-bold text-sm">{fmtRp(detail.total_cash_sales)}</p></div></div>
                <div className="flex items-center gap-2 p-2 bg-violet-50 rounded-lg"><QrCode size={16} className="text-violet-600" /><div><p className="text-xs text-slate-500">QRIS</p><p className="font-bold text-sm">{fmtRp(detail.total_qris_sales)}</p></div></div>
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg"><CreditCard size={16} className="text-blue-600" /><div><p className="text-xs text-slate-500">Debit</p><p className="font-bold text-sm">{fmtRp(detail.total_debit_sales)}</p></div></div>
                <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg"><CreditCard size={16} className="text-amber-600" /><div><p className="text-xs text-slate-500">Kredit</p><p className="font-bold text-sm">{fmtRp(detail.total_credit_sales)}</p></div></div>
              </div>

              {/* Transactions list */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Transaksi ({detailTx.length})</h3>
                {detailTx.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Belum ada transaksi di shift ini</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {detailTx.map(tx => (
                      <div key={tx.id} className={`flex items-center justify-between p-2 rounded border text-sm ${tx.is_void ? 'bg-red-50 border-red-200' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-center gap-2">
                          <ShoppingCart size={14} className="text-slate-400" />
                          <span className="font-mono text-xs">{tx.order_number}</span>
                          {tx.is_void && <Badge className="bg-red-100 text-red-700 text-xs">VOID</Badge>}
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-xs capitalize">{tx.payment_method}</Badge>
                          <span className="text-xs text-slate-400">{fmtTime(tx.created_at)}</span>
                          <span className={`font-medium ${tx.is_void ? 'text-red-500 line-through' : ''}`}>{fmtRp(tx.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.notes_open && <p className="text-sm text-slate-500"><strong>Catatan buka:</strong> {detail.notes_open}</p>}
              {detail.notes_close && <p className="text-sm text-slate-500"><strong>Catatan tutup:</strong> {detail.notes_close}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
